import React, { useEffect, useState } from 'react';
import api from '../../../api/axiosConfig';

interface DonorAnimal {
  _id: string;
  animalId: string;
  owner_phone?: string;
  species: string;
  breed: string;
  age: number;
  bloodGroup: string;
  location: string;
}

interface BloodRequest {
  _id: string;
  requestedAnimalId: string;
  owner_phone?: string;
  species: string;
  breed: string;
  age: number;
  bloodGroup: string;
  location: string;
  quantity: string;
  reason: string;
  image?: string;
  healthRecord?: {
    description: string;
    fileUrl?: string;
  };
  status: string;
  requestedAt: string;
  matchedDonors?: string[];
}

const BloodRequestsSection: React.FC = () => {
  const [requests, setRequests] = useState<BloodRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedHealth, setSelectedHealth] = useState<BloodRequest['healthRecord'] | null>(null);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  const [requestDonorMap, setRequestDonorMap] = useState<Record<string, DonorAnimal[]>>({});
  const [matchingSelections, setMatchingSelections] = useState<Record<string, string[]>>({});
  const [searchInput, setSearchInput] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const res = await api.admin_api.get<{ requests: BloodRequest[] }>('/donor/bloodrequests');
        setRequests(res.data.requests);
      } catch (err) {
        console.error('Error fetching requests:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, []);

  useEffect(() => {
    const fetchCompatibleDonors = async () => {
      const resultMap: Record<string, DonorAnimal[]> = {};

      await Promise.all(
        requests.map(async (request) => {
          try {
            const res = await api.admin_api.get<DonorAnimal[]>(
              `/donor/compatible/${request._id}`,
            );
            resultMap[request._id] = res.data;
          } catch (err) {
            // Older blood requests created before requestedAnimalId was introduced
            // cannot be matched safely and are intentionally shown with no donors.
            console.error(`Failed to load compatible donors for request ${request._id}`, err);
            resultMap[request._id] = [];
          }
        }),
      );

      setRequestDonorMap(resultMap);
    };

    if (requests.length > 0) {
      fetchCompatibleDonors();
    } else {
      setRequestDonorMap({});
    }
  }, [requests]);

  const handleApprove = (id: string) => {
    setRequests((previous) =>
      previous.map((request) =>
        request._id === id ? { ...request, status: 'Approved' } : request,
      ),
    );
  };

  const handleReject = (id: string) => {
    setRequests((previous) =>
      previous.map((request) =>
        request._id === id ? { ...request, status: 'Rejected' } : request,
      ),
    );
  };

  const handleSubmitMatch = async (requestId: string) => {
    const selected = matchingSelections[requestId];
    if (!selected || selected.length === 0) {
      setToast({ type: 'error', message: 'Please select at least one compatible donor.' });
      return;
    }

    try {
      await api.admin_api.post(`/donor/assign-match/${requestId}`, {
        matchedDonors: selected,
      });

      setRequests((previous) =>
        previous.map((request) =>
          request._id === requestId ? { ...request, status: 'Matched' } : request,
        ),
      );

      setMatchingSelections((previous) => {
        const updated = { ...previous };
        delete updated[requestId];
        return updated;
      });

      setSearchInput((previous) => {
        const updated = { ...previous };
        delete updated[requestId];
        return updated;
      });

      setToast({ type: 'success', message: 'Match assigned successfully!' });
    } catch (err: any) {
      console.error('Failed to assign match:', err);
      setToast({
        type: 'error',
        message: err?.response?.data?.message || 'Failed to assign donor match.',
      });
    }
  };

  const filterDonors = (requestId: string, donors: DonorAnimal[]) => {
    const search = (searchInput[requestId] || '').trim().toLowerCase();
    if (!search) return donors;

    return donors.filter((donor) =>
      [donor.species, donor.breed, donor.bloodGroup, donor.location, donor.owner_phone]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(search)),
    );
  };

  return (
    <div>
      {toast && (
        <div className={`global-toast ${toast.type}`}>
          <span>{toast.message}</span>
          <button className="toast-close" onClick={() => setToast(null)}>×</button>
        </div>
      )}

      <h2>Blood Requests</h2>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Species</th>
                <th>Breed</th>
                <th>Age</th>
                <th>Blood Group</th>
                <th>Quantity</th>
                <th>Reason</th>
                <th>Location</th>
                <th>Contact</th>
                <th>Status</th>
                <th>Action</th>
                <th>Matching</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((request) => {
                const compatibleDonors = requestDonorMap[request._id] || [];
                const visibleDonors = filterDonors(request._id, compatibleDonors);

                return (
                  <tr key={request._id}>
                    <td>{request.species}</td>
                    <td>{request.breed}</td>
                    <td>{request.age}</td>
                    <td>{request.bloodGroup}</td>
                    <td>{request.quantity}</td>
                    <td>{request.reason}</td>
                    <td>{request.location}</td>
                    <td>{request.owner_phone}</td>
                    <td>{request.status}</td>
                    <td>
                      {request.status === 'Matched' ? (
                        <span style={{ color: 'green', fontSize: '17px', fontWeight: 'bold' }}>
                          ✔ Completed
                        </span>
                      ) : (
                        <div className="right-wrong-btn-div">
                          <button
                            className="action-button edit"
                            onClick={() => handleApprove(request._id)}
                            disabled={request.status !== 'Pending'}
                          >
                            ✔
                          </button>
                          <button
                            className="action-button delete"
                            onClick={() => handleReject(request._id)}
                            disabled={request.status !== 'Pending'}
                          >
                            ✘
                          </button>
                        </div>
                      )}
                    </td>

                    <td>
                      {request.status === 'Approved' ? (
                        <div className="matching-field">
                          <input
                            type="text"
                            className="matching-search"
                            placeholder="Search compatible donors..."
                            value={searchInput[request._id] || ''}
                            onChange={(event) =>
                              setSearchInput((previous) => ({
                                ...previous,
                                [request._id]: event.target.value,
                              }))
                            }
                          />

                          <div
                            style={{
                              maxHeight: '150px',
                              overflowY: 'auto',
                              border: '1px solid #ccc',
                              padding: '8px',
                              borderRadius: '6px',
                            }}
                          >
                            {visibleDonors.length === 0 ? (
                              <div>No compatible donors found</div>
                            ) : (
                              visibleDonors.map((donor) => (
                                <div key={donor._id} style={{ marginBottom: '6px' }}>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <input
                                      type="checkbox"
                                      checked={(matchingSelections[request._id] || []).includes(donor._id)}
                                      onChange={(event) => {
                                        const previousSelections = matchingSelections[request._id] || [];
                                        const updatedSelections = event.target.checked
                                          ? [...previousSelections, donor._id]
                                          : previousSelections.filter((id) => id !== donor._id);

                                        setMatchingSelections((previous) => ({
                                          ...previous,
                                          [request._id]: updatedSelections,
                                        }));
                                      }}
                                    />
                                    {donor.species} ({donor.breed}) · {donor.bloodGroup} · {donor.location}
                                  </label>
                                </div>
                              ))
                            )}
                          </div>

                          <button
                            className="form-button match-submit"
                            onClick={() => handleSubmitMatch(request._id)}
                            disabled={(matchingSelections[request._id] || []).length === 0}
                          >
                            Submit Match
                          </button>
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selectedHealth && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Health Record</h3>
            <p><strong>Description:</strong> {selectedHealth.description}</p>
            {selectedHealth.fileUrl && (
              <img
                src={`${process.env.REACT_APP_MEDIA_URL}${selectedHealth.fileUrl}`}
                alt="Health File"
                style={{ width: '100%', borderRadius: '6px' }}
              />
            )}
            <button className="form-button cancel-button" onClick={() => setSelectedHealth(null)}>
              Close
            </button>
          </div>
        </div>
      )}

      {selectedImageUrl && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Uploaded Image</h3>
            <img
              src={selectedImageUrl}
              alt="Blood Request"
              style={{ width: '100%', borderRadius: '6px', marginTop: '10px' }}
            />
            <button className="form-button cancel-button" onClick={() => setSelectedImageUrl(null)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BloodRequestsSection;
